import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Building2, User, Scale, Banknote, FileText, Calendar, MessageSquare,
  Target, FolderOpen, Clock, AlertTriangle, Phone, Mail, MapPin, Loader2, Save,
  RefreshCw, Gavel, ExternalLink, CheckCircle, XCircle, Shield, AlertOctagon,
  Users, Navigation, Search, Send, Printer, FileCheck, History, Timer, Bell,
  ChevronRight, Edit, Plus, Download, Stamp, Building, CreditCard, AlertCircle,
  Flag, TrendingUp, Pause, MoreHorizontal
} from "lucide-react";
import { 
  getLegalCasesV2, getLegalCaseLoanDiskData, getXeroTruePrincipal, getXeroStatus, 
  getLegalCaseCompanyInfo, getLegalCaseCreditsafeReport, getLegalCaseNearestCourt, 
  searchCourtsByPostcode, getCaseTimeline, updateCaseStage, updateCaseActionStatus,
  addCaseNote, addCaseTimelineEntry, assignCaseHandler, getLegalCaseHandlers,
  refreshAirtableNotes,
} from "@/lib/api";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const SF_BLUE = "#3B70E0";

// Action status configuration
const ACTION_STATUS_CONFIG = {
  action_required: { label: "Action Required", color: "bg-red-500", textColor: "text-red-600", bgLight: "bg-red-50", icon: AlertCircle },
  waiting: { label: "Waiting", color: "bg-yellow-500", textColor: "text-yellow-600", bgLight: "bg-yellow-50", icon: Clock },
  chase_required: { label: "Chase Required", color: "bg-orange-500", textColor: "text-orange-600", bgLight: "bg-orange-50", icon: Bell },
  on_hold: { label: "On Hold", color: "bg-gray-400", textColor: "text-gray-600", bgLight: "bg-gray-50", icon: Pause },
  complete: { label: "Complete", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50", icon: CheckCircle },
};

// Legal stages
const LEGAL_STAGES = [
  { value: "dn_lba", label: "DN/LBA", jurisdiction: "all" },
  { value: "sd2_served", label: "SD2 Served", jurisdiction: "england_wales" },
  { value: "sd2_substituted", label: "SD2 - Substituted Service", jurisdiction: "england_wales" },
  { value: "form5_served", label: "Form 5 Served (Scotland)", jurisdiction: "scotland" },
  { value: "awaiting_challenge", label: "Awaiting Challenge Period", jurisdiction: "england_wales" },
  { value: "awaiting_challenge_scot", label: "Awaiting Challenge (Scotland)", jurisdiction: "scotland" },
  { value: "can_petition", label: "Can File Petition", jurisdiction: "england_wales" },
  { value: "can_sequestrate", label: "Can Apply for Sequestration", jurisdiction: "scotland" },
  { value: "petition_filed", label: "Petition Filed", jurisdiction: "england_wales" },
  { value: "sequestration_applied", label: "Sequestration Applied", jurisdiction: "scotland" },
  { value: "hearing_scheduled", label: "Hearing Scheduled", jurisdiction: "england_wales" },
  { value: "bankruptcy_order", label: "Bankruptcy Order Made", jurisdiction: "england_wales" },
  { value: "award_of_sequestration", label: "Award of Sequestration", jurisdiction: "scotland" },
  { value: "ccj", label: "CCJ Obtained", jurisdiction: "england_wales" },
  { value: "charging_order_interim", label: "Interim Charging Order", jurisdiction: "england_wales" },
  { value: "charging_order_final", label: "Final Charging Order", jurisdiction: "england_wales" },
  { value: "winding_up", label: "Winding-up Petition", jurisdiction: "all" },
  { value: "iva", label: "IVA Proposed/Accepted", jurisdiction: "all" },
  { value: "payment_plan", label: "Payment Plan Agreed", jurisdiction: "all" },
  { value: "collections_restructured", label: "Back to Collections", jurisdiction: "all" },
  { value: "write_off", label: "Written Off", jurisdiction: "all" },
  { value: "fully_paid", label: "Fully Paid", jurisdiction: "all" },
];

// Timeline action types for quick entry
const TIMELINE_ACTIONS = [
  { value: "dn_issued", label: "Default Notice Issued", icon: FileText },
  { value: "lba_sent", label: "LBA Sent", icon: Send },
  { value: "sd2_served", label: "SD2 Served (E&W)", icon: Stamp },
  { value: "form5_served", label: "Form 5 Served (Scotland)", icon: Stamp },
  { value: "sd2_attempted", label: "SD2 - Attempted Service", icon: AlertTriangle },
  { value: "petition_filed", label: "Petition Filed", icon: FileCheck },
  { value: "hearing_attended", label: "Hearing Attended", icon: Building },
  { value: "payment_received", label: "Payment Received", icon: Banknote },
  { value: "phone_call", label: "Phone Call Made", icon: Phone },
  { value: "email_sent", label: "Email Sent", icon: Mail },
  { value: "letter_posted", label: "Letter Posted", icon: Printer },
  { value: "court_contacted", label: "Court Contacted", icon: Gavel },
  { value: "process_server", label: "Process Server Instructed", icon: Users },
  { value: "messenger_at_arms", label: "Messenger at Arms (Scotland)", icon: Users },
  { value: "other", label: "Other Action", icon: MoreHorizontal },
];

// Letter templates (for future DocuPilot/Postworks integration)
const LETTER_TEMPLATES = [
  { id: "pdn", label: "Pre-Default Notice (PDN)", description: "14-day warning before default" },
  { id: "dn", label: "Default Notice (DN)", description: "Formal default notification" },
  { id: "lba", label: "Letter Before Action (LBA)", description: "7-day notice before legal proceedings" },
  { id: "sd2", label: "Statutory Demand (SD2)", description: "England & Wales statutory demand" },
  { id: "form5", label: "Form 5 (Scotland)", description: "Scottish statutory demand" },
  { id: "n215", label: "N215 - Substituted Service", description: "Application for substituted service" },
  { id: "bankruptcy_petition", label: "Bankruptcy Petition", description: "Petition to bankrupt debtor" },
  { id: "72hr", label: "72-Hour Letter", description: "Final warning letter" },
];

// Email templates
const EMAIL_TEMPLATES = [
  { id: "chase_payment", label: "Chase Payment", to: "customer" },
  { id: "payment_plan_offer", label: "Payment Plan Offer", to: "customer" },
  { id: "court_submission", label: "Court Submission", to: "court" },
  { id: "process_server_instruction", label: "Process Server Instructions", to: "process_server" },
  { id: "messenger_at_arms", label: "Messenger at Arms Instructions", to: "messenger_at_arms" },
  { id: "solicitor_instruction", label: "Solicitor Instructions", to: "solicitor" },
];

export default function LegalCaseFile() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // External data
  const [loandiskData, setLoandiskData] = useState(null);
  const [xeroData, setXeroData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [creditsafeData, setCreditsafeData] = useState(null);
  const [courtData, setCourtData] = useState(null);
  const [xeroConnected, setXeroConnected] = useState(false);
  
  // Timeline & case management
  const [timeline, setTimeline] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [caseHandlers, setCaseHandlers] = useState([]);
  // Audience filter — multi-select, mirrors the Collections timeline contract.
  const [audienceSelected, setAudienceSelected] = useState(() => new Set(["internal", "external"]));
  const [countsByAudience, setCountsByAudience] = useState({ internal: 0, external: 0 });
  
  // Court search
  const [manualPostcode, setManualPostcode] = useState("");
  const [searchingCourt, setSearchingCourt] = useState(false);

  // Airtable historical notes sync
  const [airtableSyncing, setAirtableSyncing] = useState(false);
  
  // Dialog states
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [letterDialogOpen, setLetterDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [handlerDialogOpen, setHandlerDialogOpen] = useState(false);
  
  // Dialog form data
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedHandler, setSelectedHandler] = useState("");
  const [selectedTimelineAction, setSelectedTimelineAction] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [dialogNote, setDialogNote] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailTo, setEmailTo] = useState("");

  // Load case data
  const loadCase = useCallback(async () => {
    try {
      setLoading(true);
      const [casesData, handlersData] = await Promise.all([
        getLegalCasesV2({ search: caseId }),
        getLegalCaseHandlers()
      ]);
      
      // Find case by ID
      let foundCase = (casesData.cases || []).find(c => c.id === caseId);
      
      // If not found by ID, search more broadly
      if (!foundCase) {
        const allCases = await getLegalCasesV2({});
        foundCase = (allCases.cases || []).find(c => c.id === caseId);
      }
      
      if (!foundCase) {
        toast.error("Case not found");
        navigate("/legal/case-tracker");
        return;
      }
      
      setCaseData(foundCase);
      setCaseHandlers(handlersData.case_handlers || []);
      setSelectedStage(foundCase.legal_stage_v2 || "");
      setSelectedStatus(foundCase.action_status || "action_required");
      setSelectedHandler(foundCase.case_handler_id || "");
      
      // Load timeline
      const timelineData = await getCaseTimeline(caseId);
      setTimeline(timelineData.timeline || []);
      setDeadlines(timelineData.deadlines || []);
      setCountsByAudience(timelineData.counts_by_audience || { internal: 0, external: 0 });
      
      setLoading(false);
      
      // Load external data in background
      loadExternalData(foundCase);
    } catch (error) {
      toast.error("Failed to load case");
      console.error(error);
      setLoading(false);
    }
  }, [caseId, navigate]);

  const loadExternalData = async (caseItem) => {
    // Check Xero connection
    try {
      const status = await getXeroStatus();
      setXeroConnected(status.connected && !status.expired);
    } catch {
      setXeroConnected(false);
    }
    
    // Companies House
    getLegalCaseCompanyInfo(caseItem.id)
      .then(data => setCompanyInfo(data))
      .catch(() => setCompanyInfo(null));
    
    // Creditsafe
    setCreditsafeData({ loading: true });
    getLegalCaseCreditsafeReport(caseItem.id)
      .then(data => setCreditsafeData(data))
      .catch(() => setCreditsafeData({ error: true }));
    
    // Court Finder
    if (caseItem.guarantor_address) {
      setCourtData({ loading: true });
      getLegalCaseNearestCourt(caseItem.id, 'guarantor')
        .then(data => setCourtData(data))
        .catch(() => setCourtData({ error: true }));
    }
    
    // LoanDisk
    setLoandiskData({ loading: true });
    getLegalCaseLoanDiskData(caseItem.id)
      .then(data => setLoandiskData(data))
      .catch(() => setLoandiskData({ error: true }));
    
    // Xero
    if (caseItem.sf_number) {
      setXeroData({ loading: true });
      getXeroTruePrincipal(caseItem.sf_number)
        .then(data => setXeroData(data))
        .catch(() => setXeroData(null));
    }
  };

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  // Handler functions
  const handleUpdateStage = async () => {
    if (!selectedStage) return;
    try {
      const result = await updateCaseStage(caseId, selectedStage, dialogNote || null);
      toast.success(`Stage updated. ${result.deadlines_created?.length || 0} deadlines created.`);
      setStageDialogOpen(false);
      setDialogNote("");
      loadCase();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedStatus) return;
    try {
      await updateCaseActionStatus(caseId, selectedStatus, dialogNote || null);
      toast.success("Status updated");
      setStatusDialogOpen(false);
      setDialogNote("");
      loadCase();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleAssignHandler = async () => {
    if (!selectedHandler) return;
    try {
      const handler = caseHandlers.find(h => h.id === selectedHandler);
      await assignCaseHandler(caseId, selectedHandler, handler?.name || "Unknown");
      toast.success(`Assigned to ${handler?.name}`);
      setHandlerDialogOpen(false);
      loadCase();
    } catch (error) {
      toast.error("Failed to assign handler");
    }
  };

  const handleAddNote = async () => {
    if (!dialogNote.trim()) return;
    try {
      await addCaseNote(caseId, dialogNote);
      toast.success("Note added");
      setNoteDialogOpen(false);
      setDialogNote("");
      loadCase();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleAddTimelineEntry = async () => {
    if (!selectedTimelineAction) return;
    try {
      await addCaseTimelineEntry(caseId, selectedTimelineAction, dialogNote || null);
      toast.success("Timeline entry added");
      setTimelineDialogOpen(false);
      setSelectedTimelineAction("");
      setDialogNote("");
      loadCase();
    } catch (error) {
      toast.error("Failed to add timeline entry");
    }
  };

  const handleSendLetter = async () => {
    // Placeholder for Postworks integration
    toast.info("Letter generation coming soon - Postworks integration pending");
    setLetterDialogOpen(false);
  };

  const handleSendEmail = async () => {
    // Placeholder for email integration
    toast.info("Email sending coming soon - Outlook integration pending");
    setEmailDialogOpen(false);
  };

  const handleManualCourtSearch = async () => {
    if (!manualPostcode.trim()) return;
    setSearchingCourt(true);
    try {
      const result = await searchCourtsByPostcode(manualPostcode.trim(), "", 5);
      setCourtData({ ...result, manual_search: true });
    } catch {
      toast.error("Failed to search for courts");
    } finally {
      setSearchingCourt(false);
    }
  };

  // Pull historical legal CaseNotes from Airtable (legacy migration).
  // Idempotent — same row can be re-imported safely.
  const handleRefreshAirtable = async () => {
    setAirtableSyncing(true);
    try {
      const res = await refreshAirtableNotes(caseId);
      if (res?.ok === false) {
        toast.error(res?.error || "No matching Airtable record found");
      } else {
        const imported = res?.imported_count ?? 0;
        const deadlines = res?.deadline_count ?? 0;
        if (imported === 0 && deadlines === 0) {
          toast.info(res?.note || "Airtable matched but no new notes to import");
        } else {
          toast.success(`Imported ${imported} note(s)${deadlines ? `, ${deadlines} deadline(s)` : ""} from Airtable`);
        }
      }
      loadCase();
    } catch (err) {
      const detail = errorDetailMessage(err, err?.message || "Failed to sync Airtable notes");
      toast.error(detail);
    } finally {
      setAirtableSyncing(false);
    }
  };

  // Formatters
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "£0";
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const getStatusConfig = (status) => ACTION_STATUS_CONFIG[status] || ACTION_STATUS_CONFIG.action_required;
  const filteredStages = LEGAL_STAGES.filter(s => s.jurisdiction === "all" || s.jurisdiction === (caseData?.jurisdiction || "england_wales"));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p>Case not found</p>
        <Button onClick={() => navigate("/legal/case-tracker")} className="mt-4">
          Back to Tracker
        </Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(caseData.action_status);
  const StatusIcon = statusConfig.icon;
  const ld = loandiskData?.loandisk_data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/legal/case-tracker")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{caseData.company_name}</h1>
              <Badge className={`${statusConfig.color} text-white`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
              {caseData.jurisdiction === "scotland" && (
                <Badge variant="outline" className="border-blue-300 text-blue-700">
                  <MapPin className="w-3 h-3 mr-1" /> Scotland
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">{caseData.sf_number}</span>
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" /> {caseData.director || "No director"}
              </span>
              {caseData.case_handler_name && (
                <span className="flex items-center gap-1 text-blue-600">
                  <Users className="w-3 h-3" /> {caseData.case_handler_name}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => loadCase()}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <p className="text-xs text-red-600 uppercase font-medium">Balance</p>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(caseData.balance)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Principal</p>
            <p className="text-2xl font-bold">{formatCurrency(caseData.principal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">LoanDisk Balance</p>
            <p className="text-2xl font-bold text-blue-600">
              {loandiskData?.loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (ld ? formatCurrency(ld.loan_balance) : "—")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Xero Principal</p>
            <p className="text-2xl font-bold text-emerald-600">
              {xeroData?.loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (xeroData?.found ? formatCurrency(xeroData.true_principal?.principal_at_risk) : "—")}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Days in Legal</p>
            <p className="text-2xl font-bold">{ld?.days_past_due || "—"}</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Bar */}
      <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-blue-700">Quick Actions:</span>
            <Button size="sm" variant="outline" onClick={() => setStageDialogOpen(true)}>
              <TrendingUp className="w-3 h-3 mr-1" /> Update Stage
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStatusDialogOpen(true)}>
              <Flag className="w-3 h-3 mr-1" /> Set Status
            </Button>
            <Button size="sm" variant="outline" onClick={() => setHandlerDialogOpen(true)}>
              <Users className="w-3 h-3 mr-1" /> Assign Handler
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <Button size="sm" variant="outline" onClick={() => setTimelineDialogOpen(true)}>
              <Plus className="w-3 h-3 mr-1" /> Log Action
            </Button>
            <Button size="sm" variant="outline" onClick={() => setNoteDialogOpen(true)}>
              <MessageSquare className="w-3 h-3 mr-1" /> Add Note
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <Button size="sm" onClick={() => setLetterDialogOpen(true)}>
              <Printer className="w-3 h-3 mr-1" /> Send Letter
            </Button>
            <Button size="sm" onClick={() => setEmailDialogOpen(true)}>
              <Mail className="w-3 h-3 mr-1" /> Send Email
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Next Deadline Alert */}
      {caseData.next_deadline && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-200 flex items-center justify-center">
                  <Timer className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm text-amber-600 font-medium">Next Deadline</p>
                  <p className="text-lg font-bold text-amber-800">{caseData.next_deadline.label}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-700">{formatDate(caseData.next_deadline.date)}</p>
                <p className="text-sm text-amber-600">
                  {Math.ceil((new Date(caseData.next_deadline.date) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Timeline & Deadlines</TabsTrigger>
          <TabsTrigger value="documents">Legal Notes</TabsTrigger>
          <TabsTrigger value="contacts">Contacts & Courts</TabsTrigger>
          <TabsTrigger value="company">Company Info</TabsTrigger>
          <TabsTrigger value="financial">Financial Data</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Case Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                  Case Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 max-h-64 overflow-y-auto">
                  <p className="text-sm whitespace-pre-wrap">{caseData.case_notes || "No notes recorded."}</p>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setNoteDialogOpen(true)} data-testid="legal-case-add-note-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Note
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshAirtable}
                    disabled={airtableSyncing}
                    data-testid="legal-case-sync-airtable-btn"
                    title="Pull historical CaseNotes from Airtable into this case timeline (idempotent)"
                  >
                    {airtableSyncing
                      ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      : <Download className="w-3 h-3 mr-1" />}
                    Sync from Airtable
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Key Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Key Dates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">PDN Date</p>
                    <p className="font-semibold">{formatDate(caseData.pdn_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">DN Date</p>
                    <p className="font-semibold">{formatDate(caseData.dn_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">LBA Date</p>
                    <p className="font-semibold">{formatDate(caseData.lba_date)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Court Date</p>
                    <p className="font-semibold">{formatDate(caseData.court_date)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Stage card removed per user request — cases are
                managed from the legal diary/calendar instead. The Next
                Action callout moves to its own slim card. */}
            {caseData.next_action && (
              <Card>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase">Next Action</p>
                      <p className="font-medium">{caseData.next_action}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Guarantors */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-teal-600" />
                  Guarantors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-teal-600" />
                      <span className="font-medium">{caseData.director || "Primary Guarantor"}</span>
                    </div>
                    {caseData.guarantor_address && (
                      <p className="text-sm text-muted-foreground flex items-start gap-2">
                        <MapPin className="w-3 h-3 mt-1 flex-shrink-0" />
                        {caseData.guarantor_address}
                      </p>
                    )}
                    {caseData.guarantor_email && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <Mail className="w-3 h-3" /> {caseData.guarantor_email}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Timeline */}
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5 text-blue-600" />
                      Activity Timeline
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleRefreshAirtable}
                        disabled={airtableSyncing}
                        data-testid="legal-timeline-sync-airtable-btn"
                        title="Import legacy Airtable CaseNotes into this timeline"
                      >
                        {airtableSyncing
                          ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          : <Download className="w-3 h-3 mr-1" />}
                        Sync Airtable
                      </Button>
                      <Button size="sm" onClick={() => setTimelineDialogOpen(true)}>
                        <Plus className="w-3 h-3 mr-1" /> Log Action
                      </Button>
                    </div>
                  </div>
                  {/* Audience filter — multi-select, mirrors Collections timeline */}
                  <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t" data-testid="legal-timeline-audience-filter">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mr-1">
                      View
                    </span>
                    {[
                      { key: "internal", label: "Internal", count: countsByAudience.internal || 0, activeTone: "bg-orange-100 text-orange-900 border-orange-400 ring-1 ring-orange-300" },
                      { key: "external", label: "External-friendly", count: countsByAudience.external || 0, activeTone: "bg-emerald-100 text-emerald-900 border-emerald-400 ring-1 ring-emerald-300" },
                    ].map(opt => {
                      const active = audienceSelected.has(opt.key);
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setAudienceSelected(prev => {
                            const next = new Set(prev);
                            if (next.has(opt.key)) {
                              if (next.size === 1) return next;   // never deselect both
                              next.delete(opt.key);
                            } else {
                              next.add(opt.key);
                            }
                            return next;
                          })}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${active ? opt.activeTone : "bg-white text-slate-400 border-slate-200 line-through"}`}
                          data-testid={`legal-timeline-audience-${opt.key}`}
                          title={active ? `Click to hide ${opt.label.toLowerCase()} events` : `Click to show ${opt.label.toLowerCase()} events`}
                        >
                          {opt.label} <span className="ml-1 tabular-nums opacity-80">{opt.count}</span>
                        </button>
                      );
                    })}
                    {audienceSelected.size !== 2 && (
                      <button
                        type="button"
                        onClick={() => setAudienceSelected(new Set(["internal", "external"]))}
                        className="px-2 py-1 rounded-full text-[11px] font-medium text-slate-600 hover:text-slate-900 hover:bg-white border border-dashed border-slate-300"
                        title="Show both audiences"
                        data-testid="legal-timeline-audience-reset"
                      >
                        Show all
                      </button>
                    )}
                    <span className="ml-auto text-[11px] text-muted-foreground" data-testid="legal-timeline-visible-count">
                      {timeline.filter(e => audienceSelected.has(e.audience || "external")).length} of {timeline.length} entries
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const visible = timeline.filter(e => audienceSelected.has(e.audience || "external"));
                    if (visible.length === 0) {
                      return (
                        <p className="text-center text-muted-foreground py-8">
                          {timeline.length === 0
                            ? "No timeline entries yet"
                            : "All entries hidden — re-enable an audience chip to see them."}
                        </p>
                      );
                    }
                    return (
                      <div className="space-y-4">
                        {visible.map((entry, idx) => {
                          const ActionIcon = TIMELINE_ACTIONS.find(a => a.value === entry.action_type)?.icon || FileText;
                          const isInternal = entry.audience === "internal";
                          return (
                            <div key={idx} className="flex gap-4">
                              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                isInternal ? "bg-orange-100" : "bg-blue-100"
                              }`}>
                                <ActionIcon className={`w-5 h-5 ${isInternal ? "text-orange-700" : "text-blue-600"}`} />
                              </div>
                              <div className="flex-1 pb-4 border-b last:border-0">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium">{entry.label || entry.action_type?.replace(/_/g, ' ')}</p>
                                    {isInternal && (
                                      <Badge variant="outline" className="text-[9px] bg-orange-50 border-orange-300 text-orange-800 px-1.5 py-0">Internal</Badge>
                                    )}
                                  </div>
                                  <span className="text-xs text-muted-foreground">{formatDateTime(entry.date)}</span>
                                </div>
                                {entry.notes && (
                                  <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                                )}
                                {entry.auto_deadlines_created > 0 && (
                                  <Badge variant="outline" className="mt-2 text-xs">
                                    {entry.auto_deadlines_created} deadline(s) created
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Deadlines */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Timer className="w-5 h-5 text-orange-600" />
                    Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {deadlines.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">No deadlines set</p>
                  ) : (
                    <div className="space-y-3">
                      {deadlines.map((dl, idx) => {
                        const isPast = new Date(dl.date) < new Date();
                        const isSoon = !isPast && Math.ceil((new Date(dl.date) - new Date()) / (1000 * 60 * 60 * 24)) <= 7;
                        return (
                          <div 
                            key={idx} 
                            className={`p-3 rounded-lg border ${isPast ? 'bg-red-50 border-red-200' : isSoon ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'}`}
                          >
                            <p className={`text-sm font-medium ${isPast ? 'text-red-700' : isSoon ? 'text-amber-700' : ''}`}>
                              {dl.label}
                            </p>
                            <p className={`text-xs ${isPast ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {formatDate(dl.date)}
                              {isPast && " (PASSED)"}
                              {isSoon && !isPast && " (SOON)"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Generate & Send Documents
              </CardTitle>
              <CardDescription>
                {caseData.jurisdiction === "scotland" 
                  ? "Scottish process: Form 5, Messenger at Arms service, Sequestration" 
                  : "England & Wales process: SD2, Process Server service, Bankruptcy Petition"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {LETTER_TEMPLATES.filter(t => {
                  // Filter based on jurisdiction
                  if (caseData.jurisdiction === "scotland" && t.id === "sd2") return false;
                  if (caseData.jurisdiction !== "scotland" && t.id === "form5") return false;
                  return true;
                }).map(template => (
                  <Card key={template.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedLetter(template.id); setLetterDialogOpen(true); }}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{template.label}</p>
                          <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                        </div>
                        <Send className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Postworks Integration Pending</span>
                </div>
                <p className="text-sm text-amber-600">
                  Letter posting via Postworks is not built yet — a credential will not enable it.
                  Documents can be generated here, but every letter must be posted and its service
                  recorded by your existing manual process.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts & Courts Tab */}
        <TabsContent value="contacts" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nearest Courts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gavel className="w-5 h-5 text-purple-600" />
                  Nearest Courts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {courtData?.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : courtData?.courts?.length > 0 ? (
                  <div className="space-y-3">
                    {courtData.courts.slice(0, 3).map((court, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border">
                        <p className="font-medium">{court.name}</p>
                        <p className="text-sm text-muted-foreground">{court.address}</p>
                        {court.distance && (
                          <Badge variant="outline" className="mt-2 text-xs">{court.distance} miles</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-muted-foreground text-sm mb-4">No courts found automatically</p>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Enter postcode..." 
                        value={manualPostcode} 
                        onChange={(e) => setManualPostcode(e.target.value)}
                        className="flex-1"
                      />
                      <Button onClick={handleManualCourtSearch} disabled={searchingCourt}>
                        {searchingCourt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Process Servers / Messengers */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-teal-600" />
                  {caseData.jurisdiction === "scotland" ? "Messenger at Arms" : "Process Servers"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-slate-50 rounded-lg border text-center">
                  <p className="text-sm text-muted-foreground">
                    {caseData.jurisdiction === "scotland" 
                      ? "Scottish statutory demands must be served by a Messenger at Arms appointed by the court."
                      : "SD2 service can be done by a certified process server."}
                  </p>
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setEmailDialogOpen(true)}>
                    <Mail className="w-3 h-3 mr-1" /> Email Instructions
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Company Info Tab */}
        <TabsContent value="company" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Companies House */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Companies House
                </CardTitle>
              </CardHeader>
              <CardContent>
                {companyInfo ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Company Number</p>
                        <p className="font-mono">{companyInfo.company_number}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Status</p>
                        <Badge variant={companyInfo.company_status === "active" ? "default" : "destructive"}>
                          {companyInfo.company_status}
                        </Badge>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Incorporated</p>
                        <p className="font-medium">{formatDate(companyInfo.date_of_creation)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">SIC Codes</p>
                        <p className="font-mono text-xs">{companyInfo.sic_codes?.join(", ") || "—"}</p>
                      </div>
                    </div>
                    {companyInfo.has_insolvency_history && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-red-700">
                          <AlertOctagon className="w-4 h-4" />
                          <span className="font-medium">Has Insolvency History</span>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Creditsafe */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  Credit Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                {creditsafeData?.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : creditsafeData?.report ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg text-center">
                      <p className="text-xs text-emerald-600 uppercase">Credit Score</p>
                      <p className="text-4xl font-bold text-emerald-700">{creditsafeData.report.credit_score || "—"}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Credit Limit</p>
                        <p className="font-semibold">{formatCurrency(creditsafeData.report.credit_limit)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">CCJs</p>
                        <p className="font-semibold">{creditsafeData.report.ccj_count || 0}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Credit report not available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LoanDisk Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Scale className="w-5 h-5 text-blue-600" />
                  LoanDisk Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loandiskData?.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : ld ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600">Loan Balance</p>
                      <p className="text-xl font-bold text-blue-700">{formatCurrency(ld.loan_balance)}</p>
                    </div>
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-xs text-red-600">Arrears</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(ld.arrears_amount)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Principal</p>
                      <p className="font-semibold">{formatCurrency(ld.loan_principal)}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-muted-foreground">Days Past Due</p>
                      <p className="font-semibold">{ld.days_past_due || "—"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg col-span-2">
                      <p className="text-xs text-muted-foreground">Loan Status</p>
                      <Badge>{ld.loan_status}</Badge>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">LoanDisk data not available</p>
                )}
              </CardContent>
            </Card>

            {/* Xero Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Banknote className="w-5 h-5 text-emerald-600" />
                  Xero True Principal
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!xeroConnected ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground text-sm">Xero not connected</p>
                    <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate("/settings")}>
                      Connect Xero
                    </Button>
                  </div>
                ) : xeroData?.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : xeroData?.found ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-emerald-50 rounded-lg text-center">
                      <p className="text-xs text-emerald-600 uppercase">Principal at Risk</p>
                      <p className="text-3xl font-bold text-emerald-700">
                        {formatCurrency(xeroData.true_principal?.principal_at_risk)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Original Principal</p>
                        <p className="font-semibold">{formatCurrency(xeroData.true_principal?.original_principal)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Paid to Date</p>
                        <p className="font-semibold">{formatCurrency(xeroData.true_principal?.paid_to_date)}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">Invoice not found in Xero</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {/* Update Stage Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Legal Stage</DialogTitle>
            <DialogDescription>
              {caseData.jurisdiction === "scotland" ? "Scottish process" : "England & Wales process"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger>
                <SelectValue placeholder="Select stage..." />
              </SelectTrigger>
              <SelectContent>
                {filteredStages.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea placeholder="Add notes (optional)..." value={dialogNote} onChange={(e) => setDialogNote(e.target.value)} />
            <p className="text-xs text-muted-foreground">💡 Deadlines will be auto-calculated based on the stage</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStageDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleUpdateStage} disabled={!selectedStage}>Update Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Action Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="action_required">🔴 Action Required</SelectItem>
                <SelectItem value="waiting">🟡 Waiting</SelectItem>
                <SelectItem value="chase_required">🟠 Chase Required</SelectItem>
                <SelectItem value="on_hold">⚪ On Hold</SelectItem>
                <SelectItem value="complete">✅ Complete</SelectItem>
              </SelectContent>
            </Select>
            <Textarea placeholder="Add notes (optional)..." value={dialogNote} onChange={(e) => setDialogNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleUpdateStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Handler Dialog */}
      <Dialog open={handlerDialogOpen} onOpenChange={setHandlerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Case Handler</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedHandler} onValueChange={setSelectedHandler}>
              <SelectTrigger>
                <SelectValue placeholder="Select handler..." />
              </SelectTrigger>
              <SelectContent>
                {caseHandlers.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name} ({h.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHandlerDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAssignHandler} disabled={!selectedHandler}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Case Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea placeholder="Enter your note..." value={dialogNote} onChange={(e) => setDialogNote(e.target.value)} rows={5} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteDialogOpen(false); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={!dialogNote.trim()}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Timeline Entry Dialog */}
      <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Action</DialogTitle>
            <DialogDescription>Record an action taken on this case</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedTimelineAction} onValueChange={setSelectedTimelineAction}>
              <SelectTrigger>
                <SelectValue placeholder="Select action type..." />
              </SelectTrigger>
              <SelectContent>
                {TIMELINE_ACTIONS.filter(a => {
                  if (caseData.jurisdiction === "scotland" && a.value === "sd2_served") return false;
                  if (caseData.jurisdiction !== "scotland" && (a.value === "form5_served" || a.value === "messenger_at_arms")) return false;
                  return true;
                }).map(a => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea placeholder="Add notes (optional)..." value={dialogNote} onChange={(e) => setDialogNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTimelineDialogOpen(false); setSelectedTimelineAction(""); setDialogNote(""); }}>Cancel</Button>
            <Button onClick={handleAddTimelineEntry} disabled={!selectedTimelineAction}>Log Action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Letter Dialog */}
      <Dialog open={letterDialogOpen} onOpenChange={setLetterDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate & Send Letter</DialogTitle>
            <DialogDescription>
              {LETTER_TEMPLATES.find(t => t.id === selectedLetter)?.description || "Select a letter template"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedLetter} onValueChange={setSelectedLetter}>
              <SelectTrigger>
                <SelectValue placeholder="Select letter type..." />
              </SelectTrigger>
              <SelectContent>
                {LETTER_TEMPLATES.filter(t => {
                  if (caseData.jurisdiction === "scotland" && t.id === "sd2") return false;
                  if (caseData.jurisdiction !== "scotland" && t.id === "form5") return false;
                  return true;
                }).map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Postworks integration pending. Letters will be generated but must be posted manually.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLetterDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendLetter} disabled={!selectedLetter}>
              <Printer className="w-4 h-4 mr-1" /> Generate Letter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Template</Label>
              <Select value={selectedEmail} onValueChange={setSelectedEmail}>
                <SelectTrigger>
                  <SelectValue placeholder="Select email template..." />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>To</Label>
              <Input placeholder="recipient@email.com" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea placeholder="Email content..." value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} />
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-700">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Outlook integration pending. Emails will be prepared but must be sent manually via legal@simplyfunded.co.uk
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSendEmail}>
              <Mail className="w-4 h-4 mr-1" /> Prepare Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
